import { useTranslation } from 'react-i18next'

import { Box, Grid, Text } from '@enechain/ecloud-designsystem'

import { formatMwSum } from '~/models/mwDisplay'
import { formatPctSum } from '~/models/pctDisplay'
import {
  type FillRateTone,
  type IntradayExecutionKpi,
  fillRateTone,
} from '~/pages/IntradayBidding/models/intradayExecutionKpi'

type Props = {
  kpi: IntradayExecutionKpi
}

const DASH = '—'

const TONE_COLOR = {
  positive: 'Text.positiveMid',
  warning: 'Text.warningHigh',
  negative: 'Text.negativeMid',
} as const satisfies Record<FillRateTone, string>

type KpiColor = (typeof TONE_COLOR)[FillRateTone]

/**
 * 約定履歴タブ上部の KPI カード 6 枚 (AC-BIN-021 = design `intraday-result.html` `.spot-result-kpis`)。
 * 入札件数 / 約定済件数 (N / M) / 約定量 (買) / 約定量 (売) / 加重平均約定価格 / 約定率。
 * 収支のカードは持たない (AC-BIN-079)。
 */
export const ExecutionResultKpiGrid = ({ kpi }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const fillRate = fillRateTone(kpi.fillRatePct)

  return (
    <Grid
      alignItems="stretch"
      gridGap={3}
      gridTemplateColumns="repeat(auto-fit, minmax(150px, 1fr))"
    >
      <KpiCard
        label={t('page:biddingIntraday.executionResult.kpi.bidCount')}
        value={t('page:biddingIntraday.executionResult.kpi.bidCountValue', {
          count: kpi.bidCount,
        })}
      />
      <KpiCard
        label={t('page:biddingIntraday.executionResult.kpi.executedCount')}
        value={`${kpi.executedCount} / ${kpi.bidCount}`}
      />
      <KpiCard
        label={t('page:biddingIntraday.executionResult.kpi.buyVolume')}
        unit="MW"
        value={formatMwSum(kpi.buyExecVolumeMw)}
      />
      <KpiCard
        label={t('page:biddingIntraday.executionResult.kpi.sellVolume')}
        unit="MW"
        value={formatMwSum(kpi.sellExecVolumeMw)}
      />
      {/* 約定量が 0 の時は「—」 (集計表の未約定行と同じ)。 値に「¥」 を前置するので unit は付けない。 */}
      <KpiCard
        label={t('page:biddingIntraday.executionResult.kpi.avgPrice')}
        value={
          kpi.weightedAvgPriceYenPerKwh === undefined
            ? DASH
            : `¥${kpi.weightedAvgPriceYenPerKwh.toFixed(2)}`
        }
      />
      <KpiCard
        color={fillRate === undefined ? undefined : TONE_COLOR[fillRate]}
        label={t('page:biddingIntraday.executionResult.kpi.fillRate')}
        unit="%"
        value={formatPctSum(kpi.fillRatePct)}
      />
    </Grid>
  )
}

type KpiCardProps = {
  label: string
  value: string
  unit?: string
  /** 値の文字色 (約定率の色分け)。 未指定は通常の強調色。 */
  color?: KpiColor
}

const KpiCard = ({
  label,
  value,
  unit,
  color,
}: KpiCardProps): React.ReactElement => (
  <Box
    padding={3}
    border="1px solid"
    borderColor="Border.lowEmphasis"
    borderRadius="md"
    aria-label={label}
    backgroundColor="Surface.primary"
    role="group"
  >
    {/* design `.ecl-kpi-label` = 14px/400 / text-mid-emphasis、 `.ecl-kpi-value` = 20px/700。 */}
    <Text color="Text.midEmphasis" fontSize="md">
      {label}
    </Text>
    {/* eslint-disable-next-line ebalance/no-adhoc-typography -- KPI 値 xl/bold (MarketBoardTab:587 と同文言) */}
    <Text
      color={color ?? 'Text.highEmphasis'}
      fontSize="xl"
      fontWeight="bold"
      css={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
      {unit !== undefined && (
        <Text as="span" textStyle="captionNormal" color="Text.lowEmphasis">
          {' '}
          {unit}
        </Text>
      )}
    </Text>
  </Box>
)
